import { getToken, useAuth, useClerk } from '@clerk/react';
import { setAuthTokenGetter, setUnauthorizedHandler } from '@reelplicate/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

// Registered at import time, not in an effect: child effects run before parent
// ones, so a query fired by the first page render would otherwise go out
// without a token. `getToken` reads Clerk's global instance and caches the JWT.
setAuthTokenGetter(() => getToken());

/**
 * Connects Clerk to the API client and the query cache. Renders nothing.
 * - A 401 from the API means the session is gone: sign out, back to /sign-in.
 * - Signing out drops every cached query, so the next user never sees the
 *   previous one's data.
 */
export function AuthBridge() {
  const clerk = useClerk();
  const { isLoaded, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const wasSignedIn = useRef(false);

  useEffect(() => {
    setUnauthorizedHandler(() => void clerk.signOut({ redirectUrl: '/sign-in' }));
    return () => setUnauthorizedHandler(undefined);
  }, [clerk]);

  useEffect(() => {
    if (!isLoaded) return;
    if (wasSignedIn.current && !isSignedIn) queryClient.clear();
    wasSignedIn.current = isSignedIn;
  }, [isLoaded, isSignedIn, queryClient]);

  return null;
}
